import theme from './theme.json' with { type: 'json' };

/**
 * Perplexicon's global color system. Values are named by interface role,
 * never by a page or game, so the palette can change without component edits.
 * Contrast-sensitive pairings are documented in docs/UI_GUIDELINES.md.
 */
export const semanticColors = theme.colors;

export type SemanticColorToken = keyof typeof semanticColors;

/** Stable CSS custom-property names consumed by Tailwind and game scopes. */
export const colorCssVariables = {
  page: '--px-color-page',
  frame: '--px-color-frame',
  frameElevated: '--px-color-frame-elevated',
  background: '--px-color-background',
  surface: '--px-color-surface',
  surfaceElevated: '--px-color-surface-elevated',
  surfaceAlt: '--px-color-surface-alt',
  parchment: '--px-color-parchment',
  inset: '--px-color-inset',
  overlay: '--px-color-overlay',
  textPrimary: '--px-color-text-primary',
  textSecondary: '--px-color-text-secondary',
  textMuted: '--px-color-text-muted',
  textOnDark: '--px-color-text-on-dark',
  textOnDarkMuted: '--px-color-text-on-dark-muted',
  textOnBrand: '--px-color-text-on-brand',
  brand: '--px-color-brand',
  brandHighlight: '--px-color-brand-highlight',
  accent: '--px-color-accent',
  accentHover: '--px-color-accent-hover',
  accentActive: '--px-color-accent-active',
  wood: '--px-color-wood',
  border: '--px-color-border',
  borderStrong: '--px-color-border-strong',
  divider: '--px-color-divider',
  focusRing: '--px-color-focus-ring',
  link: '--px-color-link',
  linkHover: '--px-color-link-hover',
  interactive: '--px-color-interactive',
  interactiveHover: '--px-color-interactive-hover',
  interactiveActive: '--px-color-interactive-active',
  interactiveDisabled: '--px-color-interactive-disabled',
  danger: '--px-color-danger',
  dangerHover: '--px-color-danger-hover',
  dangerSurface: '--px-color-danger-surface',
  success: '--px-color-success',
  successSurface: '--px-color-success-surface',
  warning: '--px-color-warning',
  warningSurface: '--px-color-warning-surface',
  info: '--px-color-info',
  infoSurface: '--px-color-info-surface',
  // Canonical semantic role aliases (docs/UI_GUIDELINES.md §2). These name a
  // UI role rather than a palette slot so components never reach for a raw
  // hex or a near-duplicate: `surfaceMuted` is the recessed parchment shade,
  // and `correct`/`present`/`incorrect` are the evaluated-guess states shared
  // by every word game's board and the platform `Keyboard` component.
  surfaceMuted: '--px-color-surface-muted',
  correct: '--px-color-correct',
  correctSurface: '--px-color-correct-surface',
  present: '--px-color-present',
  presentSurface: '--px-color-present-surface',
  incorrect: '--px-color-incorrect',
  incorrectSurface: '--px-color-incorrect-surface',
  focus: '--px-color-focus',
} as const satisfies Record<SemanticColorToken, `--${string}`>;

/** References with fallbacks keep the first paint usable before JS initializes. */
export const cssColorReferences = Object.fromEntries(
  Object.entries(semanticColors).map(([key, value]) => [
    key,
    `var(${colorCssVariables[key as SemanticColorToken]}, ${value})`,
  ]),
) as Record<SemanticColorToken, string>;

/** @deprecated Consume semanticColors; retained as a compatibility alias. */
export const colors = semanticColors;
